import React from 'react';
import { Grid, Box, Typography } from '@mui/material';
import { FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';
import { slimScrollbarStyles } from '../../../utils/styles';
import LabeledInput from '../../Common/LabeledInput';
import { FormValues } from '../../../utils/validations/officeDetailsForm';
import { StyledCheckbox } from '../../Bookings/SetAvailability/SetAvailabilityStep';
import CustomPhoneNumberInput from '../../CustomPhoneNumberInput/CustomPhoneNumberInput';

interface OfficeDetailsFormProps {
  formik: FormikProps<FormValues>;
  countryCode: string;
  handlePhoneNumberChange: (_pNumber: string) => void;
  hasPermissions: boolean;
}

const OfficeDetailsForm: React.FC<OfficeDetailsFormProps> = ({
  formik,
  countryCode,
  handlePhoneNumberChange,
  hasPermissions,
}) => {
  const { t } = useTranslation();

  return (
    <Grid
      display="flex"
      flexDirection="column"
      gap={1.2}
      maxWidth={400}
      p={2}
      maxHeight={465}
      sx={{ ...slimScrollbarStyles, overflowY: 'auto' }}
    >
      <Grid display="flex" flexDirection="column" sx={{ borderTop: '1px solid #e5e5e5', pt: 1 }}>
        <Typography variant="title1Bold" fontSize={14} mb={1}>
          {t('bookings.officeDetails')}
        </Typography>
        <LabeledInput
          label={t('bookings.inPersonConsultationsStep.address')}
          id="address"
          type="text"
          name="address"
          value={formik.values.address}
          handleChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.address ? formik.errors.address : undefined}
          placeholder={t('bookings.inPersonConsultationsStep.placeholder')}
          minRows={4}
          maxRows={4}
          multiline
          required
          maxLength={250}
          disabled={!hasPermissions}
        />
        <Grid display="flex" flexDirection="row" alignItems="center" ml={-1}>
          <StyledCheckbox
            onClick={() => formik.setFieldValue('isAddressPublic', !formik.values.isAddressPublic)}
            checked={formik.values.isAddressPublic}
            inputProps={{ 'aria-label': 'controlled' }}
            checkedIcon={
              <Box
                component="img"
                src="/assets/icons/check-box-checked.svg"
                width={20}
                height={20}
              />
            }
            icon={<Box component="img" src="/assets/icons/check-box.svg" width={20} height={20} />}
            disabled={!hasPermissions}
          />
          <Typography variant="meta" color="primary">
            {t('bookings.inPersonConsultationsStep.makePublic')}
          </Typography>
        </Grid>
      </Grid>
      <Grid display="flex" flexDirection="column">
        <CustomPhoneNumberInput
          name="phoneNumber"
          value={formik.values.phoneNumber || ''}
          handleChange={handlePhoneNumberChange}
          onBlur={formik.handleBlur}
          error={formik.touched.phoneNumber ? formik.errors.phoneNumber : undefined}
          countryCode={countryCode}
          disabled={!hasPermissions}
        />
        <Grid display="flex" flexDirection="row" alignItems="center" ml={-1}>
          <StyledCheckbox
            inputProps={{ 'aria-label': 'controlled' }}
            onClick={() =>
              formik.setFieldValue('isPhoneNumberPublic', !formik.values.isPhoneNumberPublic)
            }
            checked={formik.values.isPhoneNumberPublic}
            checkedIcon={
              <Box
                component="img"
                src="/assets/icons/check-box-checked.svg"
                width={20}
                height={20}
              />
            }
            icon={<Box component="img" src="/assets/icons/check-box.svg" width={20} height={20} />}
            disabled={!hasPermissions}
          />
          <Typography variant="meta" color="primary">
            {t('bookings.inPersonConsultationsStep.makePublic')}
          </Typography>
        </Grid>
      </Grid>
      <Typography variant="meta" color="primary" sx={{ borderBottom: '1px solid #E8E8E8', pb: 2 }}>
        {t('bookings.inPersonConsultationsStep.footNote')}
      </Typography>
    </Grid>
  );
};

export default OfficeDetailsForm;
